import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ApiClient, CreationStatus } from '../api/client';
import { messages } from '../messages';
import WebApp from '@twa-dev/sdk';
import { VideoPlayer } from './creation/VideoPlayer';
import { ConfigurationPanel } from './creation/ConfigurationPanel';
import { ErrorMessage } from './common/ErrorMessage';
import { KovrificationStrategy, StrategyContentProps } from './strategies/KovrificationStrategy';
import { BestMomentsStrategy } from './strategies/BestMomentsStrategy';
import { debugLog } from '../utils/logger';
import { SharingOptions } from './creation/SharingOptions';
import { AccessRequest } from './creation/AccessRequest';
import { useTranslation } from 'react-i18next';
import { StrategyType, getStrategyType } from '../constants/strategy';


interface UserInfo {
  id: number;
  username: string;
  first_name: string;
  last_name?: string;
  photo_url?: string;
  activated: boolean;
  balance: number;
}

interface CreationDetailsProps {
  onBalanceUpdate: (newBalance: number) => void;
}

export function CreationDetails({ onBalanceUpdate }: CreationDetailsProps) {
  const { id } = useParams<{ id: string }>();
  const [creation, setCreation] = useState<Awaited<ReturnType<typeof ApiClient.getCreation>> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [strategyType, setStrategyType] = useState<StrategyType | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const { t } = useTranslation();

  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchUserInfo = useCallback(async () => {
    try {
      const info = await ApiClient.getUserInfo();
      setUserInfo(info);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    }
  }, []);

  const fetchCreation = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const response = await ApiClient.getCreation(id);
      setCreation(response);
      setStrategyType(getStrategyType(response.strategy, response.type));
      // Reset access denied status if fetch is successful
      setAccessDenied(false);
    } catch (error) {
      console.error('Failed to fetch creation:', error);
      
      // Check if the error message indicates restricted access (thrown by ApiClient)
      if (error instanceof Error && error.message === "Creation is not shared") { 
        debugLog('Caught access restriction error from ApiClient, setting accessDenied.');
        setAccessDenied(true);
      } else {
        // Handle other errors (network issues, unexpected API errors, etc.)
        console.error('Unexpected error during creation fetch:', error);
        setError(messages.errors.loadCreation);
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCreation();
    fetchUserInfo();
  }, [fetchCreation, fetchUserInfo]);

  // Set up periodic updates when creation is in processing state
  useEffect(() => {
    // Only set up polling if creation exists and is in a processing state
    if (creation && (creation.status === CreationStatus.IN_PROGRESS || creation.status === CreationStatus.PENDING)) {
      debugLog('Setting up periodic updates for creation:', creation.id);
      
      // Fetch creation details every 10 seconds
      const updateInterval = setInterval(() => {
        debugLog('Fetching latest creation details...');
        fetchCreation();
      }, 10000);
      
      // Clean up interval on unmount or when status changes
      return () => {
        debugLog('Clearing periodic updates');
        clearInterval(updateInterval);
      };
    }
  }, [creation, fetchCreation]);

  const handleBack = () => {
    // Simply use direct navigation to projects
    window.location.href = '/projects';
  };

  const handleDelete = async () => {
    if (!id || isDeleting) return;

    const handleConfirmDelete = (buttonId?: string) => {
      if (buttonId === 'delete') {
        setIsDeleting(true);
        ApiClient.deleteCreation(id);
        window.location.href = '/';
      }
    }

    const tg = WebApp;
    try {
      await tg.showPopup({
        title: t('creationDetails.deletePopup.title'),
        message: t('creationDetails.deletePopup.message'),
        buttons: [
          { id: 'delete', type: 'destructive', text: t('creationDetails.deletePopup.deleteButton') },
          { id: 'cancel', type: 'default', text: t('creationDetails.deletePopup.cancelButton') }
        ],
      }, handleConfirmDelete);

      setIsDeleting(true);
      await ApiClient.deleteCreation(id);
      window.location.href = '/';
    } catch (err) {
      const error = err as { message?: string };
      if (error?.message?.includes('popup closed')) {
        // User cancelled the deletion
        return;
      }
      
      console.error('Failed to delete creation:', error);
      await tg.showPopup({
        title: t('errors.errorTitle'),
        message: t('errors.deleteCreation'),
        buttons: [{ type: 'ok' }]
      });
      // Navigate to home even if deletion failed
      window.location.href = '/';
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePaymentComplete = async (newBalance: number) => {
    if (userInfo) {
      setUserInfo({ ...userInfo, balance: newBalance });
      onBalanceUpdate(newBalance);
    }
    await fetchCreation();
  };

  // Update only the sharing status locally
  const handleCreationUpdate = (isShared: boolean) => {
    setCreation(prevCreation => 
      prevCreation ? { ...prevCreation, is_shared: isShared } : null
    );
  };

  const renderStrategyContent = () => {
    if (!creation || !userInfo || !id) return null;

    const inputVideo = creation.uploaded_files[0];
    const outputVideo = creation.result_files[0];
    
    // Common props for all strategy components
    const strategyProps: StrategyContentProps = {
      creation,
      userInfo,
      inputVideo,
      outputVideo,
      onDownload: (url, filename) => {
        // This function is required by the type but won't be used
        // The VideoPlayer will use the direct download link instead
        debugLog('Download requested for:', url, filename);
      },
      onPaymentComplete: handlePaymentComplete,
      creationId: id
    };

    // Render the appropriate strategy component based on the strategy type
    switch (strategyType) {
      case StrategyType.KOVRIFICATION:
        return <KovrificationStrategy {...strategyProps} />;
      case StrategyType.BEST_MOMENTS:
        return <BestMomentsStrategy {...strategyProps} />;
      // Add more cases here as more strategies are added
      default:
        return null;
    }
  };

  // Check loading state first
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-500 animate-pulse text-lg">
        {messages.home.loading}
      </div>
    );
  }

  // If access is denied, show the request component regardless of other errors or creation state
  if (accessDenied && id) {
    // Ensure userInfo is available before rendering AccessRequest if needed
    return <AccessRequest creationId={id} userInfo={userInfo} />; // Pass userInfo
  }

  // If there's a general error (and access wasn't denied), show the error message
  if (error) {
    return <ErrorMessage message={error} />;
  }

  // If loading is finished, access isn't denied, there's no specific error, but creation is still null,
  // it means the creation wasn't found or there was an issue not caught as a specific error.
  if (!creation) {
    return <ErrorMessage message={messages.errors.loadCreation} />;
  }

  // If none of the above, proceed to render the creation details
  const inputVideo = creation.uploaded_files[0];
  const isOwner = !creation.owner; // If no owner property, current user is the owner

  return (
    <div className="min-h-screen bg-theme-bg relative pt-4">
      {/* Floating back button */}
      <button 
        onClick={handleBack}
        className="fixed left-4 bottom-6 z-50 p-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-white rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
        style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>arrow_back</span>
      </button>

      {/* Show sharing options button only if user is the owner and creation is completed */}
      {isOwner && creation.status === CreationStatus.COMPLETED && (
        <SharingOptions 
          creation={creation} 
          onUpdate={handleCreationUpdate}
        />
      )}

      <div className="max-w-6xl mx-auto px-4 pb-4">
        <div className="space-y-6">
          {renderStrategyContent()}

          {/* Show original footage for non-draft statuses (draft status shows it in DraftCreationHandler) */}
          {inputVideo && creation.status !== CreationStatus.DRAFT && (
            <div className="mt-8">
              <VideoPlayer
                title={t('creationDetails.originalFootageTitle')}
                videoUrl={inputVideo.url}
              />
            </div>
          )}

          {/* Only show ConfigurationPanel when not in DRAFT status and not BEST_MOMENTS or C_ROLL */}
          {creation.status !== CreationStatus.DRAFT && 
           strategyType !== StrategyType.BEST_MOMENTS && 
           strategyType !== StrategyType.KOVRIFICATION && (
            <div className="mt-8">
              <ConfigurationPanel
                config={creation.strategy}
                strategyType={strategyType || StrategyType.KOVRIFICATION}
              />
            </div>
          )}
        </div>
        
        {/* Only show delete button if user is the owner */}
        {isOwner && (
          <div className="mt-8">
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="w-full py-2.5 px-4 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('creationDetails.deletePopup.deleteButton')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
} 